/**
 * Global Dashboard for SuperAdmin
 * Shows aggregated metrics across all companies
 */
import React from 'react';
import { useQuery } from 'react-query';
import { Card, CardContent } from '../../components/ui/card';
import { Building2, Users, DollarSign, Activity } from 'lucide-react';
import { adminCompanyApi } from '../../services/companyApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const GlobalDashboard: React.FC = () => {
  const { data: metrics, isLoading, error } = useQuery(
    ['admin', 'dashboard', 'metrics'],
    () => adminCompanyApi.getDashboardMetrics(),
    {
      refetchInterval: 60000, // Refresh every minute
    }
  );

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error al cargar métricas del dashboard</p>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return null;
  }

  const totalCompanies =
    metrics.companiesByStatus.active +
    metrics.companiesByStatus.trial +
    metrics.companiesByStatus.suspended +
    metrics.companiesByStatus.cancelled;

  const totalUsers =
    metrics.usersByPlan.starter +
    metrics.usersByPlan.business +
    metrics.usersByPlan.enterprise;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard Global</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Vista general de todas las empresas y métricas del sistema
        </p>
      </div>

      {/* Main Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Companies */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 rounded-lg p-3">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Total Empresas
                </p>
                <p className="text-2xl font-bold">{totalCompanies}</p>
                <p className="text-xs text-green-600 mt-1">
                  +{metrics.newCompaniesLast30Days} últimos 30 días
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total Users */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-purple-100 rounded-lg p-3">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Total Usuarios
                </p>
                <p className="text-2xl font-bold">{totalUsers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Expenses */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 rounded-lg p-3">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Gastos del Mes
                </p>
                <p className="text-2xl font-bold">
                  {formatCurrency(metrics.totalExpensesThisMonth)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {metrics.expenseCountThisMonth} registros
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Companies */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="bg-yellow-100 rounded-lg p-3">
                <Activity className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-muted-foreground">
                  Empresas Activas
                </p>
                <p className="text-2xl font-bold">
                  {metrics.companiesByStatus.active}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {metrics.companiesByStatus.trial} en trial
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Companies by Status */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Empresas por Estado</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-3xl font-bold text-green-600">
                {metrics.companiesByStatus.active}
              </p>
              <p className="text-sm text-muted-foreground mt-1">Activas</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">
                {metrics.companiesByStatus.trial}
              </p>
              <p className="text-sm text-muted-foreground mt-1">Trial</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-3xl font-bold text-yellow-600">
                {metrics.companiesByStatus.suspended}
              </p>
              <p className="text-sm text-muted-foreground mt-1">Suspendidas</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-3xl font-bold text-red-600">
                {metrics.companiesByStatus.cancelled}
              </p>
              <p className="text-sm text-muted-foreground mt-1">Canceladas</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users by Plan */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Usuarios por Plan</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-3xl font-bold text-gray-600">
                {metrics.usersByPlan.starter}
              </p>
              <p className="text-sm text-muted-foreground mt-1">Starter</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">
                {metrics.usersByPlan.business}
              </p>
              <p className="text-sm text-muted-foreground mt-1">Business</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-3xl font-bold text-purple-600">
                {metrics.usersByPlan.enterprise}
              </p>
              <p className="text-sm text-muted-foreground mt-1">Enterprise</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top Companies by Usage */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">Top 5 Empresas por Uso</h3>
          <div className="space-y-3">
            {metrics.topCompaniesByUsage.map((company, index) => (
              <div
                key={company.companyId}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full">
                    <span className="text-sm font-bold text-blue-600">
                      {index + 1}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{company.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {company.companyId}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">
                    {formatCurrency(company.totalAmount)}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {company.expenseCount} gastos
                  </p>
                </div>
              </div>
            ))}
            {metrics.topCompaniesByUsage.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No hay datos de uso disponibles
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GlobalDashboard;
